import {
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from 'react';
import { useViewerBinaryCache } from '../../../../viewer-cache';
import { getNextThumbnailLoadIds } from './loadPlanner';
import { acquireThumbnailFromCacheCandidates } from './acquireThumbnailFromCacheCandidates';

function revokeThumbnailObjectUrl(objectUrl) {
    if (!objectUrl) {
        return;
    }

    URL.revokeObjectURL(objectUrl);
}

function releaseAllThumbnailLoads(activeThumbnailLoadsRef) {
    activeThumbnailLoadsRef.current.forEach((activeLoad) => {
        activeLoad.release();
    });
    activeThumbnailLoadsRef.current.clear();
}

function revokeAllThumbnailObjectUrls(thumbnailObjectUrls) {
    Object.values(thumbnailObjectUrls).forEach((objectUrl) => {
        revokeThumbnailObjectUrl(objectUrl);
    });
}

function reconcileThumbnailStates(currentState, activeScanIds) {
    const activeScanIdSet = new Set(activeScanIds);
    const currentScanIds = Object.keys(currentState);

    if (currentScanIds.every((scanId) => activeScanIdSet.has(scanId))) {
        return currentState;
    }

    return Object.fromEntries(
        Object.entries(currentState).filter(([scanId]) => activeScanIdSet.has(scanId)),
    );
}

function patchThumbnailState(currentState, scanId, nextState) {
    const currentThumbnailState = currentState[scanId];

    if (
        currentThumbnailState?.src === nextState.src
        && currentThumbnailState?.status === nextState.status
    ) {
        return currentState;
    }

    return {
        ...currentState,
        [scanId]: nextState,
    };
}

function thumbnailStatesReducer(currentState, action) {
    switch (action.type) {
        case 'reconcile-media-scans':
            return reconcileThumbnailStates(currentState, action.activeScanIds);
        case 'mark-loading':
            return patchThumbnailState(currentState, action.scanId, {
                src: '',
                status: 'loading',
            });
        case 'mark-ready':
            return patchThumbnailState(currentState, action.scanId, {
                src: action.src,
                status: 'ready',
            });
        case 'mark-failed':
            return patchThumbnailState(currentState, action.scanId, {
                src: '',
                status: 'failed',
            });
        default:
            return currentState;
    }
}

export function useMediaThumbnails({
    desiredLoadOrder,
    maxConcurrentLoads = 2,
    mediaScans,
}) {
    const viewerBinaryCache = useViewerBinaryCache();
    const [thumbnailStates, dispatch] = useReducer(thumbnailStatesReducer, {});
    const activeThumbnailLoadsRef = useRef(new Map());
    const activeThumbnailRequestTokensRef = useRef(new Map());
    const preferredThumbnailUrlByIdRef = useRef(new Map());
    const thumbnailObjectUrlsRef = useRef({});
    const isMountedRef = useRef(true);
    const mediaScansById = useMemo(
        () => Object.fromEntries(mediaScans.map((scan) => [scan.id, scan])),
        [mediaScans],
    );

    useEffect(() => () => {
        isMountedRef.current = false;
        releaseAllThumbnailLoads(activeThumbnailLoadsRef);
        activeThumbnailRequestTokensRef.current.clear();
        revokeAllThumbnailObjectUrls(thumbnailObjectUrlsRef.current);
        thumbnailObjectUrlsRef.current = {};
    }, []);

    useEffect(() => {
        const activeScanIds = new Set(mediaScans.map((scan) => scan.id));

        activeThumbnailLoadsRef.current.forEach((activeLoad, scanId) => {
            if (!activeScanIds.has(scanId)) {
                activeLoad.release();
                activeThumbnailLoadsRef.current.delete(scanId);
            }
        });

        Array.from(activeThumbnailRequestTokensRef.current.keys()).forEach((scanId) => {
            if (!activeScanIds.has(scanId)) {
                activeThumbnailRequestTokensRef.current.delete(scanId);
            }
        });

        Object.entries(thumbnailObjectUrlsRef.current).forEach(([scanId, objectUrl]) => {
            if (!activeScanIds.has(scanId)) {
                revokeThumbnailObjectUrl(objectUrl);
                delete thumbnailObjectUrlsRef.current[scanId];
            }
        });

        dispatch({
            activeScanIds: mediaScans.map((scan) => scan.id),
            type: 'reconcile-media-scans',
        });
    }, [mediaScans]);

    useEffect(() => {
        const nextLoadIds = getNextThumbnailLoadIds(
            desiredLoadOrder,
            thumbnailStates,
            Array.from(activeThumbnailLoadsRef.current.keys()),
            Math.max(0, maxConcurrentLoads - activeThumbnailLoadsRef.current.size),
        );

        nextLoadIds.forEach((scanId) => {
            const mediaScan = mediaScansById[scanId];

            if (!mediaScan) {
                return;
            }

            dispatch({
                scanId,
                type: 'mark-loading',
            });

            const requestToken = Symbol(scanId);
            activeThumbnailRequestTokensRef.current.set(scanId, requestToken);

            const activeLoad = acquireThumbnailFromCacheCandidates(
                mediaScan.thumbnailUrlCandidates,
                {
                    consumerId: `media:${scanId}`,
                    preferredUrl: preferredThumbnailUrlByIdRef.current.get(scanId),
                    viewerBinaryCache,
                },
            );

            activeThumbnailLoadsRef.current.set(scanId, activeLoad);

            activeLoad.promise
                .then(({ objectUrl, resolvedUrl }) => {
                    activeThumbnailLoadsRef.current.delete(scanId);

                    if (
                        !isMountedRef.current
                        || activeThumbnailRequestTokensRef.current.get(scanId) !== requestToken
                    ) {
                        revokeThumbnailObjectUrl(objectUrl);
                        return;
                    }

                    preferredThumbnailUrlByIdRef.current.set(scanId, resolvedUrl);

                    const previousObjectUrl = thumbnailObjectUrlsRef.current[scanId];

                    if (previousObjectUrl) {
                        revokeThumbnailObjectUrl(previousObjectUrl);
                    }

                    thumbnailObjectUrlsRef.current[scanId] = objectUrl;

                    dispatch({
                        scanId,
                        src: objectUrl,
                        type: 'mark-ready',
                    });
                })
                .catch((error) => {
                    activeThumbnailLoadsRef.current.delete(scanId);

                    if (viewerBinaryCache.isAbortError(error)) {
                        return;
                    }

                    if (
                        !isMountedRef.current
                        || activeThumbnailRequestTokensRef.current.get(scanId) !== requestToken
                    ) {
                        return;
                    }

                    preferredThumbnailUrlByIdRef.current.delete(scanId);

                    dispatch({
                        scanId,
                        type: 'mark-failed',
                    });
                });
        });
    }, [
        desiredLoadOrder,
        maxConcurrentLoads,
        mediaScansById,
        thumbnailStates,
        viewerBinaryCache,
    ]);

    const haveAllMediaFailed = useMemo(
        () => mediaScans.length > 0
            && mediaScans.every((scan) => thumbnailStates[scan.id]?.status === 'failed'),
        [mediaScans, thumbnailStates],
    );

    return {
        haveAllMediaFailed,
        thumbnailStates,
    };
}
